import React, { useState, useEffect, useRef } from 'react';
import { Lock, Briefcase, ChevronRight, Sparkles, X, Check } from 'lucide-react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';

export default function ServiceContextInput({ value, onChange, disabled }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestion, setSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastValue = useRef(value);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await api.get('/api/profile/service-context-status');
        setStatus(res.data);
      } catch {
        setStatus({ can_change: true, changes_remaining: 'unlimited', plan: 'free' });
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const handleBlur = () => {
    const trimmed = value.trim();
    if (trimmed.length >= 3 && trimmed !== lastValue.current && !dismissed) {
      lastValue.current = trimmed;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchSuggestion(trimmed), 400);
    }
  };

  const fetchSuggestion = async (input) => {
    setSuggesting(true);
    try {
      const res = await api.post('/api/profile/suggest-service-context', { input });
      if (res.data?.suggestion && res.data.suggestion !== input) {
        setSuggestion(res.data.suggestion);
      }
    } catch {
      // Silently fail,  suggestion is non-critical
    } finally {
      setSuggesting(false);
    }
  };

  const acceptSuggestion = () => {
    if (suggestion) {
      onChange(suggestion);
      lastValue.current = suggestion;
    }
    setSuggestion(null);
    setDismissed(true);
  };

  const dismissSuggestion = () => {
    setSuggestion(null);
    setDismissed(true);
  };

  // Reset dismissed state when value changes externally
  useEffect(() => {
    if (value !== lastValue.current) {
      setDismissed(false);
      setSuggestion(null);
    }
  }, [value]);

  const isLocked = !loading && status && !status.can_change;

  if (loading) return null;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--input-bg)',
        overflow: 'hidden',
        opacity: isLocked ? 0.7 : 1,
      }}>
        <span style={{ padding: '0 var(--space-md)', color: isLocked ? 'var(--text-tertiary)' : 'var(--text-secondary)', fontSize: 18, flexShrink: 0 }}>
          💼
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="E.g., Dental services, Web design, Solar panels"
          disabled={disabled || isLocked}
          style={{
            flex: 1, padding: 'var(--space-md) var(--space-md) var(--space-md) 0',
            border: 'none', background: 'none',
            color: 'var(--text-primary)', fontSize: 'var(--text-base)',
            outline: 'none', fontFamily: 'inherit',
            cursor: isLocked ? 'not-allowed' : 'text',
          }}
        />
        {isLocked && (
          <span style={{ padding: '0 var(--space-md)', color: 'var(--text-tertiary)', flexShrink: 0 }}>
            <Lock size={14} />
          </span>
        )}
        {suggesting && (
          <span style={{ padding: '0 var(--space-md)', flexShrink: 0 }}>
            <span style={{ width: 14, height: 14, border: '2px solid var(--accent-primary)', borderTop: '2px solid transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
          </span>
        )}
      </div>

      {/* AI Suggestion */}
      {suggestion && (
        <div style={{
          marginTop: 8, padding: '10px 14px',
          background: 'var(--accent-glow)',
          border: '1px solid var(--accent-primary)',
          borderRadius: 'var(--radius-md)',
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 'var(--text-sm)', animation: 'fadeIn 0.2s ease',
        }}>
          <Sparkles size={14} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
          <span style={{ color: 'var(--text-primary)', flex: 1, lineHeight: 1.4 }}>
            <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', display: 'block', marginBottom: 2 }}>AI Suggested</span>
            {suggestion}
          </span>
          <button
            onClick={acceptSuggestion}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '5px 12px', borderRadius: 'var(--radius-sm)',
              background: 'var(--accent-primary)', color: 'white',
              border: 'none', cursor: 'pointer', fontSize: 'var(--text-xs)',
              fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}>
            <Check size={12} /> Use this
          </button>
          <button
            onClick={dismissSuggestion}
            style={{
              padding: 4, borderRadius: 'var(--radius-sm)',
              background: 'transparent', color: 'var(--text-tertiary)',
              border: 'none', cursor: 'pointer',
            }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Locked Overlay with Upgrade Button */}
      {isLocked && (
        <div style={{
          marginTop: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px', background: 'var(--warning-bg)',
          borderRadius: 'var(--radius-sm)', border: '1px solid var(--warning)',
          fontSize: 11,
        }}>
          <span style={{ color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Lock size={10} />
            {status.plan === 'free'
              ? 'Service context locked on Free plan'
              : `Change limit reached (${status.changes_used}/${status.changes_limit})`}
          </span>
          <button
            onClick={() => navigate('/dashboard/billing?tab=plans')}
            style={{
              background: 'var(--accent-gradient)', color: 'white',
              border: 'none', borderRadius: 4, padding: '3px 10px',
              cursor: 'pointer', fontSize: 10, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
            }}>
            Upgrade <ChevronRight size={10} />
          </button>
        </div>
      )}


    </div>
  );
};